import {useState, useEffect, useMemo} from 'react';
import inRange from 'lodash/inRange';

import {ApiError, captureApiError} from 'src/utils/errors';


export type ResourceState<T> =
  | {
      result: null;
      error: null;
      isLoading: boolean;
    }
  | {
      result: T;
      error: null;
      isLoading: boolean;
    }
  | {
      result: null;
      error: Error;
      isLoading: boolean;
    };

// TODO (kyle): this should eventually use Suspense
export function useResource<T>(
  resource: () => Promise<T>,
  options:
    | unknown[]
    | {
        deps: unknown[];
        onError?: (arg1: Error) => unknown;
      },
): ResourceState<T> {
  const [state, setState] = useState<ResourceState<T>>(defaultState);

  const {deps, onError} = Array.isArray(options)
    ? {deps: options, onError: null}
    : options;

  useEffect(() => {
    if (!state.isLoading) {
      setState((state) => ({
        ...state,
        isLoading: true,
      }));
    }

    resource().then(
      (result: T) => {
        setState({
          result,
          error: null,
          isLoading: false,
        });
      },
      (error: Error) => {
        // NOTE (kyle): we only capture 400 errors because they are generally
        // the client's fault (not the server's).
        if (error instanceof ApiError) {
          const {status} = error.response;
          if (inRange(status, 400, 500) && ![404, 401].includes(status)) {
            captureApiError(error);
          }
        }

        setState({
          result: null,
          error,
          isLoading: false,
        });

        if (onError) {
          onError(error);
        }
      },
    );
  }, deps);

  return state;
}

export default useResource;

export const defaultState = {
  result: null,
  error: null,
  isLoading: true,
} as const;

export function useSuspenseResource<R>(
  callback: () => Promise<R>,
  deps: unknown[],
): () => R {
  return useMemo(() => {
    let loading = true;
    // @ts-expect-error - TS7034 - Variable 'error' implicitly has type 'any' in some locations where its type cannot be determined.
    let error;
    // @ts-expect-error - TS7034 - Variable 'data' implicitly has type 'any' in some locations where its type cannot be determined.
    let data;

    const promise = callback().then(
      (result) => {
        data = result;
        loading = false;
      },
      (caughtError) => {
        error = caughtError;
        loading = false;
      },
    );

    return () => {
      if (loading) {
        throw promise;
      }

      // @ts-expect-error - TS7005 - Variable 'error' implicitly has an 'any' type.
      if (error != null) {
        // @ts-expect-error - TS7005 - Variable 'error' implicitly has an 'any' type.
        throw error;
      }

      // @ts-expect-error - TS7005 - Variable 'data' implicitly has an 'any' type.
      return data;
    };
  }, deps);
}
